import {
  CheckboxProps,
  DatePickerProps,
  Form,
  FormItemProps,
  InputProps,
  RadioGroupProps,
  SelectProps,
  SwitchProps,
  TimePickerProps,
} from 'antd';
import { CheckboxGroupProps } from 'antd/es/checkbox';
import classNames from 'classnames';
import { memo, useCallback, useMemo, useRef } from 'react';
import {
  Control,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import {
  controlledInputComponents,
  ControlledInputRefType,
  OmitControlControlledInputProps,
} from '../components/RHFControlledInput';
import { formItemPropsKeys } from '../type';
import { isFieldRequired } from '../zod.utils';
import { useFormSchema } from './useFormSchema';
import '../components/RHFControlledInput/styles.scss';
import { RangePickerProps } from 'antd/es/date-picker';

type BaseControlledInputProps<FV extends FieldValues> = Omit<
  FormItemProps,
  'rules' | 'name'
> &
  UseControllerProps<FV> &
  ControlledInputRefType & {
    inputClassName?: string;
  };

type InputControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & InputProps;

type SelectControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & SelectProps;

type RadioGroupControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & RadioGroupProps;

type CheckboxControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & CheckboxProps;

type CheckboxGroupControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & CheckboxGroupProps;

type DatePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & DatePickerProps;

type TimePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & TimePickerProps;

type RangePickerControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & RangePickerProps;

type SwitchControlledInputProps<FV extends FieldValues> =
  BaseControlledInputProps<FV> & SwitchProps;

type Schema = z.ZodTypeAny | FieldValues;

export type zodSchemaType<S extends Schema> = S extends z.ZodTypeAny
  ? z.infer<S>
  : S;

// Define the type for the ControlledInput with all compound components
export type ControlledInputComponent<S extends Schema> = React.FC<
  OmitControlControlledInputProps<zodSchemaType<S>>
> & {
  Input: React.FC<InputControlledInputProps<zodSchemaType<S>>>;
  Textarea: React.FC<InputControlledInputProps<zodSchemaType<S>>>;
  Number: React.FC<InputControlledInputProps<zodSchemaType<S>>>;
  Select: React.FC<SelectControlledInputProps<zodSchemaType<S>>>;
  RadioGroup: React.FC<RadioGroupControlledInputProps<zodSchemaType<S>>>;
  Switch: React.FC<SwitchControlledInputProps<zodSchemaType<S>>>;
  Checkbox: React.FC<CheckboxControlledInputProps<zodSchemaType<S>>>;
  CheckboxGroup: React.FC<CheckboxGroupControlledInputProps<zodSchemaType<S>>>;
  DatePicker: React.FC<DatePickerControlledInputProps<zodSchemaType<S>>>;
  TimePicker: React.FC<TimePickerControlledInputProps<zodSchemaType<S>>>;
  RangePicker: React.FC<RangePickerControlledInputProps<zodSchemaType<S>>>;
};

const useBuildControlledInput = <S extends Schema>(props: {
  control: Control<zodSchemaType<S>>;
}): ControlledInputComponent<S> => {
  const { control } = props;

  const { t, i18n } = useTranslation();

  const controlRef = useRef(control);

  const ControlledInput = useMemo(() => {
    const BaseControlledInput = (
      props: OmitControlControlledInputProps<zodSchemaType<S>>,
    ) => {
      const {
        name,
        rules,
        value,
        required,
        className,
        inputType,
        inputClassName,
        inputRef: userInputRef,
        onChange,
        ...rest
      } = props;

      const formItemProps = useMemo(
        () =>
          Object.entries(props).reduce((acc, [key, value]) => {
            if (formItemPropsKeys.includes(key)) {
              acc[key] = value;
            }
            return acc;
          }, {} as any),
        [props],
      );

      const {
        field,
        fieldState: { error },
      } = useController({ name, control: controlRef.current, rules });

      const Component = useMemo(
        () => controlledInputComponents[inputType ?? 'input'],
        [inputType],
      );

      const handleChange = useCallback(
        (e: any, option?: any) => {
          onChange?.(e, option);
          field.onChange(
            (inputType === 'checkbox' ? e.target.checked : e) ?? null,
            option,
          );
        },
        [field, onChange, inputType],
      );

      const handleBlur = useCallback(
        (e: any) => {
          if (
            inputType !== 'checkboxGroup' &&
            inputType !== 'checkbox' &&
            inputType !== 'switch'
          ) {
            props?.onBlur?.(e);
          }
          field.onBlur();
        },
        [field, inputType, props],
      );

      const formSchema = useFormSchema();
      const zodRequired = isFieldRequired(formSchema, name);
      const requiredRule = !!(rules?.required ?? required ?? zodRequired);

      const componentProps = useMemo(() => {
        const baseProps = {
          ...rest,
          ...field,
        };

        if (inputType === 'number') {
          return { ...baseProps, type: 'number' };
        }

        if (inputType === 'checkbox') {
          return { ...baseProps, checked: field.value };
        }

        return baseProps;
      }, [field, inputType, rest]);

      const errorMessage = i18n.exists(error?.message ?? '')
        ? t(error?.message ?? '')
        : error?.message;

      return (
        <Form.Item
          {...formItemProps}
          help={errorMessage}
          required={requiredRule}
          validateStatus={error ? 'error' : ''}
          className={classNames(className, 'rhf-controlled-input')}
        >
          <Component
            {...componentProps}
            ref={userInputRef}
            onBlur={handleBlur}
            onChange={handleChange}
            className={classNames(inputClassName)}
          />
        </Form.Item>
      );
    };

    const MemorizedControlledInput = memo(
      BaseControlledInput,
    ) as unknown as ControlledInputComponent<S>;

    /* -------------------------------------------------------------------------- */
    /*                             Compound Components                            */
    /* -------------------------------------------------------------------------- */

    MemorizedControlledInput.Input = memo(function Input(
      props: InputControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="input" />;
    }) as React.FC<InputControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.Textarea = memo(function Textarea(
      props: InputControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="textarea" />;
    }) as React.FC<InputControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.Number = memo(function Number(
      props: InputControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="number" />;
    }) as React.FC<InputControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.Select = memo(function Select(
      props: SelectControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="select" />;
    }) as React.FC<SelectControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.RadioGroup = memo(function RadioGroup(
      props: RadioGroupControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="radioGroup" />;
    }) as React.FC<RadioGroupControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.Switch = memo(function Switch(
      props: SwitchControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="switch" />;
    }) as React.FC<SwitchControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.Checkbox = memo(function Checkbox(
      props: CheckboxControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="checkbox" />;
    }) as React.FC<CheckboxControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.CheckboxGroup = memo(function CheckboxGroup(
      props: CheckboxGroupControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="checkboxGroup" />;
    }) as React.FC<CheckboxGroupControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.DatePicker = memo(function DatePicker(
      props: DatePickerControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="datePicker" />;
    }) as React.FC<DatePickerControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.TimePicker = memo(function TimePicker(
      props: TimePickerControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="timePicker" />;
    }) as React.FC<TimePickerControlledInputProps<zodSchemaType<S>>>;

    MemorizedControlledInput.RangePicker = memo(function RangePicker(
      props: RangePickerControlledInputProps<zodSchemaType<S>>,
    ) {
      return <BaseControlledInput {...props} inputType="rangePicker" />;
    }) as React.FC<RangePickerControlledInputProps<zodSchemaType<S>>>;

    return MemorizedControlledInput;
  }, [i18n, t]);

  return ControlledInput;
};

export default useBuildControlledInput;
